import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, RefreshCw } from 'lucide-react';
import { termLabel } from '../utils/labFilters';
import useAnalyticsData from '../hooks/useAnalyticsData';
import { AnalyticsTabs, AnalyticsTable, AnalyticsOverview, AnalyticsDetail, TableToolbar, Loading, QueryError } from '../components/AnalyticsPanels';
import './Analytics.css';

const VIEWS = [
  { key: 'overview', label: 'Overview' }, { key: 'students', label: 'Students' },
  { key: 'labs', label: 'Labs' }, { key: 'ips', label: 'Addresses' }, { key: 'signals', label: 'Access signals' },
];
const SCOPE_KEYS = ['term', 'courseCode', 'labId', 'from', 'to'];
const DETAIL_KEYS = ['entity', 'id', 'dt', 'dp', 'dq', 'ds', 'dd', 'dl'];
const TABLE_KEYS = { page: 'page', limit: 'limit', search: 'q', sort: 'sort', direction: 'direction' };
const DETAIL_TABLE_KEYS = { tab: 'dt', page: 'dp', limit: 'dl', search: 'dq', sort: 'ds', direction: 'dd' };

function readTable(params, detail = false) {
  const keys = detail ? DETAIL_TABLE_KEYS : TABLE_KEYS;
  const size = Number(params.get(keys.limit));
  return { page: Math.max(1, parseInt(params.get(keys.page), 10) || 1), limit: [25, 50, 100].includes(size) ? size : 25,
    search: params.get(keys.search) || '', sort: params.get(keys.sort) || '', direction: params.get(keys.direction) === 'asc' ? 'asc' : 'desc' };
}

function LabPicker({ scope, onChange, refreshRef }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const root = useRef(null);
  const trigger = useRef(null);
  const query = useAnalyticsData('options', { term: scope.term, courseCode: scope.courseCode, labId: scope.labId, search, page, limit: 25 });
  refreshRef.current = query.refresh;
  useEffect(() => { const timer = setTimeout(() => { setSearch(input.trim()); setPage(1); }, 300); return () => clearTimeout(timer); }, [input]);
  useEffect(() => { setInput(''); setSearch(''); setPage(1); setOpen(false); }, [scope.term, scope.courseCode]);
  useEffect(() => {
    if (!open) return undefined;
    root.current.querySelector('input')?.focus();
    const outside = (event) => { if (!root.current.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [open]);
  return <>
    <label>Term<select value={scope.term || ''} onChange={(event) => onChange({ term: event.target.value, labId: '' })}><option value="">All terms</option>{query.data?.terms.map((term) => <option key={term} value={term}>{termLabel(term)}</option>)}</select></label>
    <label>Course<select value={scope.courseCode || ''} onChange={(event) => onChange({ courseCode: event.target.value, labId: '' })}><option value="">All courses</option>{query.data?.courses.map((course) => <option key={course} value={course}>{course}</option>)}</select></label>
    <div className="analytics-lab-picker" ref={root} onKeyDown={(event) => {
      if (event.key === 'Escape') { setOpen(false); trigger.current?.focus(); }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || !open) return;
      const options = [...root.current.querySelectorAll('[role="option"]')];
      const index = options.indexOf(document.activeElement);
      const next = event.key === 'ArrowDown' ? (index + 1) % options.length : (index - 1 + options.length) % options.length;
      if (options[next]) { event.preventDefault(); options[next].focus(); }
    }}>
      <span id="analytics-lab-label">Lab</span>
      <button ref={trigger} type="button" className="btn analytics-lab-trigger" aria-labelledby="analytics-lab-label analytics-lab-value" aria-expanded={open} aria-haspopup="listbox" onClick={() => setOpen(!open)}>
        <span id="analytics-lab-value">{scope.labId ? query.data?.selectedLab?.name || 'Selected lab' : 'All labs'}</span><ChevronDown size={15} aria-hidden="true" />
      </button>
      {open && <div className="analytics-lab-menu">
        <input type="search" aria-label="Search labs" placeholder="Find a lab…" value={input} maxLength={100} onChange={(event) => setInput(event.target.value)} />
        {query.isError ? <QueryError query={query} /> : !query.data ? <Loading label="Loading labs…" /> : <>
          <div className="analytics-lab-options" role="listbox" aria-label="Labs">
            <button type="button" role="option" aria-selected={!scope.labId} onClick={() => { onChange({ labId: '' }); setOpen(false); trigger.current?.focus(); }}>All labs</button>
            {query.data.labs.map((lab) => <button key={lab.id} type="button" role="option" aria-selected={scope.labId === lab.id} onClick={() => { onChange({ labId: lab.id }); setOpen(false); trigger.current?.focus(); }}>{lab.name}</button>)}
          </div>
          <div className="analytics-lab-paging">
            <button className="btn btn-sm" type="button" aria-label="Previous labs" disabled={query.isFetching || page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
            <span>{query.data.pagination.total} matches</span>
            <button className="btn btn-sm" type="button" aria-label="Next labs" disabled={query.isFetching || page >= query.data.pagination.pages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        </>}
      </div>}
    </div>
  </>;
}

const dateInput = (iso, end = false) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '';
  if (end) date.setDate(date.getDate() - 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export default function Analytics() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const scopeRefresh = useRef(null);
  const value = params.toString();
  const scope = useMemo(() => {
    const search = new URLSearchParams(value);
    return Object.fromEntries(SCOPE_KEYS.filter((key) => search.get(key)).map((key) => [key, search.get(key)]));
  }, [value]);
  const view = VIEWS.some((item) => item.key === params.get('view')) ? params.get('view') : 'overview';
  const table = readTable(params);
  const signal = ['shared', 'multi', 'concurrent'].includes(params.get('kind')) ? params.get('kind') : 'shared';
  const kind = view === 'signals' ? signal === 'shared' ? 'ips' : signal : view;
  const bucket = params.get('bucket') === 'hour' ? 'hour' : 'day';
  const entity = ['student', 'ip'].includes(params.get('entity')) && params.get('id') ? { type: params.get('entity'), id: params.get('id') } : null;
  const overviewQ = useAnalyticsData('overview', { ...scope, bucket }, view === 'overview');
  const listQ = useAnalyticsData(view === 'overview' ? 'students' : view, {
    ...scope, ...table, ...(view === 'signals' ? { kind: signal } : view === 'ips' && params.get('kind') ? { kind: params.get('kind') } : {}),
  }, view !== 'overview');
  const currentQuery = view === 'overview' ? overviewQ : listQ;

  const update = (changes, { resetPage = false, closeDetail = false, replace = false, state = location.state } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, entry]) => { if (entry === '' || entry === null || entry === undefined) next.delete(key); else next.set(key, String(entry)); });
    if (resetPage) next.delete('page');
    if (closeDetail) DETAIL_KEYS.forEach((key) => next.delete(key));
    navigate({ pathname: location.pathname, search: next.toString() }, { replace, state });
  };
  const changeScope = (changes) => update(changes, { resetPage: true, closeDetail: true, state: null });
  const changeTable = (changes) => update(Object.fromEntries(Object.entries(changes).map(([key, entry]) => [TABLE_KEYS[key], entry])), { replace: 'search' in changes });
  const explore = (nextView, nextKind = '') => update({ view: nextView, kind: nextKind, q: '', sort: '', direction: '', page: '' }, { closeDetail: true, state: null });
  const openEntity = (type, id) => {
    const trail = entity ? [...(location.state?.analyticsTrail || []), { search: location.search, label: `${entity.type === 'ip' ? 'address' : 'student'} ${entity.id}` }] : [];
    update({ entity: type, id, dt: '', dp: '', dq: '', ds: '', dd: '', dl: '' }, { state: {
      analyticsTrail: trail,
      analyticsDepth: entity ? (location.state?.analyticsDepth || 0) + 1 : 1,
      analyticsHistoryOrigin: entity ? Boolean(location.state?.analyticsHistoryOrigin) : true,
    } });
  };
  const closeDetail = () => {
    if (location.state?.analyticsHistoryOrigin && location.state.analyticsDepth) navigate(-location.state.analyticsDepth);
    else update({}, { closeDetail: true, replace: true, state: null });
  };
  const trail = location.state?.analyticsTrail || [];
  const detailBack = () => {
    if (!trail.length) return closeDetail();
    navigate(-1);
  };
  const range = params.get('range') || (scope.from || scope.to ? 'custom' : 'all');
  const chooseRange = (next) => {
    if (next === 'all') return changeScope({ range: '', from: '', to: '' });
    if (next === 'custom') return changeScope({ range: 'custom' });
    const from = new Date(), to = new Date();
    from.setHours(0, 0, 0, 0); from.setDate(from.getDate() - (Number(next) - 1));
    to.setHours(0, 0, 0, 0); to.setDate(to.getDate() + 1);
    changeScope({ range: next, from: from.toISOString(), to: to.toISOString() });
  };

  return <div className="analytics-page" data-testid="analytics-page">
    <div aria-hidden={entity ? true : undefined}>
      <header className="analytics-header"><div><h1>Analytics</h1><p>Explore engagement and access, one focused view at a time.</p></div>
        <button type="button" className="btn analytics-refresh" disabled={currentQuery.isFetching} onClick={() => { currentQuery.refresh(); scopeRefresh.current?.(); }}><RefreshCw size={15} aria-hidden="true" />Refresh</button>
      </header>
      <div className="analytics-scope" aria-label="Analytics scope">
        <LabPicker scope={scope} onChange={changeScope} refreshRef={scopeRefresh} />
        <label>Session dates<select value={range} onChange={(event) => chooseRange(event.target.value)}><option value="all">All retained activity</option><option value="1">Today</option><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="custom">Custom range</option></select></label>
        {range === 'custom' && <>
          <label>From<input type="date" value={dateInput(scope.from)} onChange={(event) => changeScope({ from: event.target.value ? new Date(`${event.target.value}T00:00:00`).toISOString() : '' })} /></label>
          <label>Through<input type="date" value={dateInput(scope.to, true)} onChange={(event) => { const date = event.target.value ? new Date(`${event.target.value}T00:00:00`) : null; if (date) date.setDate(date.getDate() + 1); changeScope({ to: date?.toISOString() || '' }); }} /></label>
        </>}
      </div>
      <AnalyticsTabs tabs={VIEWS} value={view} onChange={explore} panelId="analytics-panel" label="Analytics breakdown" />
      <div id="analytics-panel" role="tabpanel" aria-labelledby={`analytics-panel-tab-${view}`}>
        {view === 'overview' ? <AnalyticsOverview query={overviewQ} onExplore={explore} onStudent={(id) => openEntity('student', id)} onIP={(ip) => openEntity('ip', ip)} bucket={bucket} onBucket={(entry) => update({ bucket: entry })} /> : <>
          {view === 'signals' && <div className="analytics-toolbar"><label className="analytics-control">Signal type<select value={signal} onChange={(event) => explore('signals', event.target.value)}><option value="shared">Shared addresses</option><option value="multi">Multiple addresses</option><option value="concurrent">Overlapping sessions</option></select></label></div>}
          {view === 'ips' && <div className="analytics-toolbar"><label className="analytics-control">Address type<select value={params.get('kind') || 'all'} onChange={(event) => update({ kind: event.target.value, page: '' })}><option value="all">All addresses</option><option value="external">External</option><option value="shared">Shared external</option><option value="internal">Internal</option></select></label></div>}
          <TableToolbar kind={kind} {...table} onChange={changeTable} />
          <AnalyticsTable query={listQ} kind={kind} label={view === 'signals' ? 'Access signals' : VIEWS.find((item) => item.key === view).label}
            onPage={(page) => changeTable({ page })} onStudent={(id) => openEntity('student', id)} onIP={(ip) => openEntity('ip', ip)} onLab={(labId) => changeScope({ labId })} />
        </>}
      </div>
    </div>
    {entity && <AnalyticsDetail entity={entity} scope={scope} tab={params.get('dt')} table={readTable(params, true)}
      onChange={(changes) => update(Object.fromEntries(Object.entries(changes).map(([key, entry]) => [DETAIL_TABLE_KEYS[key], entry])), { replace: true })}
      onClose={closeDetail} onBack={detailBack} backLabel={trail.length ? `Back to ${trail[trail.length - 1].label}` : `Back to ${VIEWS.find((item) => item.key === view).label.toLowerCase()}`}
      onStudent={(id) => openEntity('student', id)} onIP={(ip) => openEntity('ip', ip)} onLab={(labId) => changeScope({ labId })} />}
  </div>;
}
